import { html, LitElement, nothing } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { repeat } from 'lit/directives/repeat.js';
import { when } from 'lit/directives/when.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { keyed } from 'lit/directives/keyed.js';
import { animate } from '@lit-labs/motion';

class WishlistMain extends LitElement {
  static get properties() {
    return {
      wishlist: { type: Object, state: true },
      products: { type: Array, state: true },
      loading: { type: Boolean, state: true },
      discount: { type: Object },
      skeletonLength: { type: Number, state: true },
      displayedProducts: { type: Array, state: true },
      itemsToShow: { type: Number, state: true },
      showLoadMore: { type: Boolean, state: true },
      isLoadingMore: { type: Boolean, state: true },
      lastDisplayedCount: { type: Number, state: true },
      removingProductIds: { type: Object, state: true },
      productPositions: { type: Object, state: true },
      renderedProductIds: { type: Object, state: true },
      isInitialLoad: { type: Boolean, state: true },
      boards: { type: Array, state: true },
      currentBoard: { type: Object, state: true },
      currentView: { type: String, state: true },
      boardProductCache: { type: Object, state: true },
      boardPreviewCache: { type: Object, state: true },
      loadingBoards: { type: Boolean, state: true },
      loadingBoardProducts: { type: Boolean, state: true },
      selectedProducts: { type: Object, state: true },
      boardDeleting: { type: Number, state: true },
      isInitialBoardLoad: { type: Boolean, state: true },
      siblingAdditionContext: { type: Object, state: true },
    };
  }

  createRenderRoot() {
    return this;
  }

  constructor() {
    super();
    const wishlist = window.wishlist.retrieve();

    this.count = wishlist.count;
    this.loading = true;
    this.itemsToShow = 20;
    this.displayedProducts = [];
    this.showLoadMore = false;
    this.isLoadingMore = false;
    this.lastDisplayedCount = 0;
    this.removingProductIds = new Set();
    this.productPositions = new Map();
    this.renderedProductIds = new Set();
    this.isInitialLoad = true;
    this.isInitializing = true;
    this.boardDeleting = null

    this._scrollThrottle = null;
    this.boards = [];
    this.currentBoard = null;
    this.selectedProducts = new Map();
    this.siblingAdditionContext = new Map();

    // Set initial view and loading states based on URL parameters
    const params = new URLSearchParams(window.location.search);
    const boardId = params.get('board');

    if (boardId) {
      this.currentView = 'board-products';
      this.initialBoardId = parseInt(boardId);
      this.loadingBoardProducts = true;
      this.loadingBoards = false;
    } else {
      this.currentView = 'board-selection';
      this.initialBoardId = null;
      this.loadingBoardProducts = false;
      this.loadingBoards = false;
    }

    this.boardProductCache = new Map();
    this.boardPreviewCache = new Map();
    this._loadingBoards = false;
    this.wishlistLoaded = false;
    this._updateTimeout = null;
    this.renderedBoardIds = new Set();
    this.isInitialBoardLoad = true;
  }

  async handleWishlistUpdate(e) {
    this.wishlist = e.detail;

    const wasInitializing = this.isInitializing;

    if (!this.wishlistLoaded) {
      this.wishlistLoaded = true;
      this.isInitializing = false;
    }

    this.invalidateAllCaches();

    if (!window.Shopify.customer_logged_in) {
      this.fetchProducts();
    } else {
      if (this.currentView === 'board-selection') {
        this.loadingBoards = true;
        await this.loadBoardsList();
        this.loadingBoards = false;
      } else if (this.currentView === 'board-products') {
        if (this.currentBoard?.id) {
          this.invalidateBoardCache(this.currentBoard.id);
          await this.fetchBoardProducts();
        } else if (this.initialBoardId) {
        }
      }
    }

    if (wasInitializing) {
      await this.handlePageState();
    }
  }

  handleWishlistMove() {
    this.selectedProducts = new Map();

    this._notifyToolbarOfSelectionChange();
  }

  handleWishlistRemove(e) {
    const removedItem = e.detail;

    if (removedItem && removedItem.productId) {
      this.selectedProducts.delete(parseInt(removedItem.productId));

      this.selectedProducts = new Map();

      this._notifyToolbarOfSelectionChange();
    }
  }

  invalidateAllCaches() {
    this.boardProductCache.clear();
    this.boardPreviewCache.clear();
    this.renderedBoardIds.clear();
  }

  invalidateBoardCache(boardId) {
    this.boardProductCache.delete(boardId);
    this.boardPreviewCache.delete(boardId);
  }

  async connectedCallback() {
    super.connectedCallback();

    window.addEventListener('popstate', this.handlePageState.bind(this));

    this.wishlist = await window.wishlist.retrieve();

    this.skeletonLength = this.wishlist.count;

    this.boundHandleWishlistUpdate = this.handleWishlistUpdate.bind(this);
    this.boundHandleWishlistRemove = this.handleWishlistRemove.bind(this);
    this.boundHandleWishlistMove = this.handleWishlistMove.bind(this);

    document.addEventListener(
      'wishlist:remove',
      this.boundHandleWishlistRemove
    );
    document.addEventListener('wishlist:move', this.boundHandleWishlistMove);
    document.addEventListener('wishlist:remove', this.boundHandleWishlistUpdate);
    document.addEventListener('wishlist:update', this.boundHandleWishlistUpdate);
    document.addEventListener('wishlist:initialise', this.boundHandleWishlistUpdate);
    document.addEventListener('product-card:selected', this._handleProductSelection.bind(this));
    document.addEventListener('wishlist:variant-updated', this.boundHandleWishlistUpdate);
    document.addEventListener('wishlist-toolbar:select-all', this._handleToolbarSelectAll.bind(this));
    document.addEventListener('wishlist-toolbar:deleted', this._handleToolbarDeleted.bind(this));
    document.addEventListener('product-card:swatch-changed', this._handleSwatchChange.bind(this));
    document.addEventListener('wishlist-toolbar:added-to-cart', this._handleItemsAddedToCart.bind(this));
    document.addEventListener('wishlist:add', this.handleSiblingContextCleanup.bind(this));
    document.addEventListener('wishlist:operation-failed', this.handleSiblingContextCleanup.bind(this));
  }

  async disconnectedCallback() {
    super.disconnectedCallback();

    if (this.boundHandleWishlistUpdate) {
      document.removeEventListener(
        'wishlist:remove',
        this.boundHandleWishlistRemove
      );
      document.removeEventListener(
        'wishlist:move',
        this.boundHandleWishlistMove
      );
      document.removeEventListener(
        'wishlist:remove',
        this.boundHandleWishlistUpdate
      );
      document.removeEventListener(
        'wishlist:update',
        this.boundHandleWishlistUpdate
      );
      document.removeEventListener(
        'wishlist:initialise',
        this.boundHandleWishlistUpdate
      );

      document.removeEventListener(
        'product-card:selected',
        this._handleProductSelection
      );
      document.removeEventListener(
        'wishlist-toolbar:select-all',
        this._handleToolbarSelectAll
      );

      document.removeEventListener(
        'wishlist:variant-updated',
        this.boundHandleWishlistUpdate
      );
      document.removeEventListener(
        'product-card:swatch-changed',
        this._handleSwatchChange
      );

      document.removeEventListener('wishlist-toolbar:added-to-cart', this._handleItemsAddedToCart);
      document.removeEventListener('wishlist:add', this.handleSiblingContextCleanup);
      document.removeEventListener('wishlist:operation-failed', this.handleSiblingContextCleanup);
    }

    window.removeEventListener('popstate', this.handlePageState.bind(this));
  }

  handleBoardCreate() {
    const createBoardModalOpenEvent = new CustomEvent(
      'modal-wishlist-create-board:open'
    );
    document.dispatchEvent(createBoardModalOpenEvent);
  }

  async handleBoardDelete(board) {
    if (board.id === 1) {
      return;
    }

    if (board.items.length === 0) {
      try {
        this.boardDeleting = board.id;
        await window.wishlist.deleteBoard(board.id);
      } catch {

      } finally {
        this.boardDeleting = null;
      }

      return;
    }

    const deleteBoardModalOpenEvent = new CustomEvent('modal-wishlist-delete-board:open', { detail: board });
    document.dispatchEvent(deleteBoardModalOpenEvent);
  }

  handleBoardEdit(board) {
    const editBoardModalOpenEvent = new CustomEvent(
      'modal-wishlist-edit-board:open',
      { detail: board }
    );
    document.dispatchEvent(editBoardModalOpenEvent);
  }

  async handlePageState() {
    const params = new URLSearchParams(window.location.search);
    const boardId = params.get('board');

    if (boardId) {
      const parsedBoardId = parseInt(boardId);

      if (this.currentView !== 'board-products') {
        this.currentView = 'board-products';
        this.loadingBoardProducts = true;
      }

      if (this.boards?.length === 0) {
        this.loadingBoards = true;
        await this.loadBoardsList();
        this.loadingBoards = false;
      }

      this.currentBoard = this.boards.find((board) => board.id === parsedBoardId);

      if (this.currentBoard) {
        await this.fetchBoardProducts();
      } else {
        // Board not found, redirect to board selection
        console.warn(`Board with ID ${parsedBoardId} not found`);
        this.currentView = 'board-selection';
        this.currentBoard = null;
        this.loadingBoards = true;
        await this.loadBoardsList();
        this.loadingBoards = false;
      }

      this.loadingBoardProducts = false;
      return;
    }

    this.currentBoard = null;
    this.currentView = 'board-selection';
    this.initialBoardId = null;

    this.clearCurrentProducts();

    if (window.Shopify.customer_logged_in) {
      this.loadingBoards = true;
      await this.loadBoardsList();
      this.loadingBoards = false;
    }
  }

  _handleItemsAddedToCart(e) {
    const addedProducts = e.detail?.addedProducts;

    if (!addedProducts) return;

    for (const originalId of addedProducts) {
      this.selectedProducts.delete(originalId);
    }

    this.selectedProducts = new Map(this.selectedProducts);

    this._notifyToolbarOfSelectionChange();
  }

  updateBoardState(boardId) {
    const params = new URLSearchParams(window.location.search);

    if (boardId) {
      params.set('board', boardId);
    } else {
      params.delete('board');
    }

    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({}, '', newUrl);

    this.handlePageState();
  }

  selectBoard(boardId) {
    if (!boardId) return;

    this.clearCurrentProducts();

    this.updateBoardState(boardId);

    window.sessionStorage.setItem('wishlist_adding_board', boardId);

    window.scrollTo({ top: 0 });
  }

  _handleProductSelection(e) {
    const { selected, productId, originalId } = e.detail;

    if (selected) {
      this.selectedProducts.set(originalId, productId);
    } else {
      this.selectedProducts.delete(originalId);
    }

    this.selectedProducts = new Map(this.selectedProducts);

    this._notifyToolbarOfSelectionChange();
  }

  _handleSwatchChange(e) {
    const {
      selected,
      originalId,
      newSiblingId,
      cardId,
    } = e.detail;

    if (selected) {
      this.selectedProducts.set(originalId, newSiblingId);
    }

    // Use cardId as key for card-specific context tracking
    const cardIdentifier = cardId || `card-${originalId}`;
    this.siblingAdditionContext.set(cardIdentifier, {
      newSiblingId: String(newSiblingId),
      originalId: String(originalId),
      timestamp: Date.now()
    });

    this.selectedProducts = new Map(this.selectedProducts);

    this._notifyToolbarOfSelectionChange();
  }

  _handleToolbarSelectAll(e) {
    const { selectAll } = e.detail;

    if (selectAll) {
      this.products.forEach((item) => {
        const originalId = item.product.id;
        const productId = item.product.id;
        this.selectedProducts.set(originalId, productId);
      });
    } else {
      this.selectedProducts.clear();
    }

    this.selectedProducts = new Map(this.selectedProducts);
  }

  _handleToolbarDeleted(e) {
    const { deletedProducts } = e.detail;

    for (const [originalId] of deletedProducts) {
      this.selectedProducts.delete(originalId);
    }

    this.selectedProducts = new Map(this.selectedProducts);

    this._notifyToolbarOfSelectionChange();
  }

  _notifyToolbarOfSelectionChange() {
    const toolbar = this.querySelector('wishlist-toolbar');
    if (toolbar) {
      toolbar.selectedProducts = new Map(this.selectedProducts);
      toolbar.selectableProductCount = this.products.length || 0;
      toolbar.requestUpdate();
    }
  }

  handleSiblingContextCleanup(event) {
    const { productId, cardId } = event.detail || {};

    if (productId) {
      // Clean up by productId (fallback)
      for (const [key, context] of this.siblingAdditionContext) {
        if (context.newSiblingId === String(productId) || context.originalId === String(productId)) {
          this.siblingAdditionContext.delete(key);
          break;
        }
      }

      // Auto-expand pagination to show newly added product
      this.expandPaginationForNewProduct(productId);
    }

    if (cardId) {
      // Clean up by cardId (preferred)
      this.siblingAdditionContext.delete(cardId);
    }
  }

  expandPaginationForNewProduct(productId) {
    if (!this.products) return;

    // Since products are reversed, newly added products appear at the beginning
    // We need to expand pagination to the next 20-item increment
    const currentTotalProducts = this.products.length;
    const newItemsToShow = Math.ceil(currentTotalProducts / 20) * 20;

    if (newItemsToShow > this.itemsToShow) {
      this.itemsToShow = newItemsToShow;
      this.updateDisplayedProducts();
    }
  }

  clearCurrentProducts() {
    this.products = [];
    this.displayedProducts = [];
    this.selectedProducts = new Map();
    this.showLoadMore = false;
    this.itemsToShow = 20; // Reset pagination
    this.isLoadingMore = false;
    this.lastDisplayedCount = 0;
  }

  goBackToBoards() {
    this.updateBoardState(null);
  }

  async fetchProducts() {
    try {
      if (this.displayedProducts.length > 0) {
        this.lastDisplayedCount = this.displayedProducts.length;
      }

      if (this.isInitialLoad) {
        this.loading = true;
      }

      const products = await window.wishlist.getProducts();

      this.products = products.reverse();

      this.updateDisplayedProducts();
    } catch (error) {
      console.error('Error fetching wishlist products:', error);
      this.products = [];
      this.displayedProducts = [];
    } finally {
      this.loading = false;
      this.isInitialLoad = false;

      this.updateComplete.then(() => {
        setTimeout(() => {
          this.notifyWishlistButtonsToUpdate();
        }, 50);
      });
    }
  }

  notifyWishlistButtonsToUpdate() {
    const wishlistButtons = this.querySelectorAll('wishlist-button');
    wishlistButtons.forEach((button) => {
      if (button.updateWishlistState) {
        button.updateWishlistState();
      }
    });
  }

  updateDisplayedProducts() {
    if (!this.products) return;

    const previousDisplayedProducts = this.displayedProducts || [];
    this.displayedProducts = this.products.slice(0, this.itemsToShow);

    const currentProductIds = new Set();
    this.displayedProducts.forEach((item, index) => {
      const productId = item.product.id;
      currentProductIds.add(productId);
      const wasAlreadyDisplayed = previousDisplayedProducts.some(
        (prev) => prev.product.id === productId
      );

      if (!this.productPositions.has(productId) || !wasAlreadyDisplayed) {
        this.productPositions.set(productId, index);
      }
    });

    for (const [productId] of this.productPositions) {
      if (!currentProductIds.has(productId)) {
        this.productPositions.delete(productId);
        this.renderedProductIds.delete(productId);
      }
    }

    if (!this.loading && this.displayedProducts.length > 0) {
      this.lastDisplayedCount = this.displayedProducts.length;
    }

    const hasMoreProducts = this.products.length > this.itemsToShow;
    const belowMaxLimit = this.itemsToShow < Infinity;
    const hasProductsToShow = this.products.length > this.itemsToShow;

    this.showLoadMore = hasMoreProducts && belowMaxLimit && hasProductsToShow;

    if (this.products.length < this.itemsToShow && this.products.length > 0) {
      // Don't override pagination expansion - keep 20-item increments
      // this.itemsToShow = Math.max(20, this.products.length);
    }
  }

  loadMore() {
    if (this.isLoadingMore || !this.showLoadMore) return;

    this.isLoadingMore = true;

    const nextItemsToShow = Math.min(
      this.itemsToShow + 20,
      Infinity,
      this.products?.length || 0
    );

    if (nextItemsToShow <= this.itemsToShow) {
      this.isLoadingMore = false;
      return;
    }

    this.itemsToShow = nextItemsToShow;

    this.updateDisplayedProducts();
    this.isLoadingMore = false;
  }

  async loadBoardsList() {
    if (this._loadingBoards) {
      return;
    }

    try {
      this._loadingBoards = true;
      this.loading = true;
      this.renderedBoardIds.clear();

      const wishlist = await window.wishlist.retrieve();

      if (!wishlist || !wishlist.boards) {
        console.warn('No boards found in wishlist');
        this.boards = [];
        return;
      }

      this.boards = wishlist.boards
        .filter((board) => board.active)
        .map((board) => ({
          id: board.id,
          name: board.name,
          emoji: board.emoji,
          colour: board.colour,
          active: board.active,
          items: board.items || [],
          itemCount: board.items ? board.items.length : 0,
        }));

      await this.loadAllBoardPreviews();
    } catch (error) {
      console.error('Failed to load boards:', error);
      this.boards = [];
    } finally {
      this.loading = false;
      this._loadingBoards = false;
      this.isInitialBoardLoad = false;
    }
  }

  async loadAllBoardPreviews() {
    const previewPromises = this.boards
      .filter(
        (board) =>
          board.items.length > 0 && !this.boardPreviewCache.has(board.id)
      )
      .map((board) => this.loadBoardPreviewProducts(board.id));

    await Promise.all(previewPromises);
  }

  async loadBoardPreviewProducts(boardId) {
    if (this.boardPreviewCache.has(boardId)) {
      const cached = this.boardPreviewCache.get(boardId);
      const board = this.boards.find((b) => b.id === boardId);
      if (cached.length === Math.min(4, board?.items?.length || 0)) {
        return cached;
      }
    }

    try {
      const board = this.boards.find((b) => b.id === boardId);
      if (!board || !board.items || board.items.length === 0) {
        this.boardPreviewCache.set(boardId, []);
        return [];
      }

      const previewProducts = await window.wishlist.getProducts(boardId);
      const limitedPreview = previewProducts.reverse().slice(0, 4);

      this.boardPreviewCache.set(boardId, limitedPreview);

      this.requestUpdate();

      return limitedPreview;
    } catch (error) {
      console.error('Failed to load board preview products:', error);
      this.boardPreviewCache.set(boardId, []);
      return [];
    }
  }

  async fetchBoardProducts() {
    if (!this.currentBoard?.id) {
      this.products = [];
      this.displayedProducts = [];
      return;
    }

    const boardId = this.currentBoard.id;

    if (this.displayedProducts.length > 0) {
      this.lastDisplayedCount = this.displayedProducts.length;
    }

    if (this.boardProductCache.has(boardId)) {
      this.products = this.boardProductCache.get(boardId);

      if (this.displayedProducts.length > 0) {
        this.lastDisplayedCount = this.displayedProducts.length;
      }

      this.updateDisplayedProducts();
      this.loading = false;
      this.isInitialLoad = false;

      this.updateComplete.then(() => {
        setTimeout(() => {
          this.notifyWishlistButtonsToUpdate();
        }, 50);
      });
      return;
    }

    try {
      this.loading = true;

      const products = await window.wishlist.getProducts(boardId);
      const reversedProducts = products.reverse();

      this.boardProductCache.set(boardId, reversedProducts);
      this.products = reversedProducts;

      this.updateDisplayedProducts();
    } catch (error) {
      console.error('Error fetching board products:', error);
      this.products = [];
      this.displayedProducts = [];
    } finally {
      this.loading = false;
      this.isInitialLoad = false;

      this.updateComplete.then(() => {
        setTimeout(() => {
          this.notifyWishlistButtonsToUpdate();
        }, 50);
      });
    }
  }

  renderSkellyCreateBoard() {
    const isNewCreateButton = !this.renderedBoardIds.has('create-board');
    this.renderedBoardIds.add('create-board');

    return html`
      <li class="wishlist_board_card"
          ${isNewCreateButton ? animate({
      in: [
        { opacity: 0 },
        { opacity: 1 }
      ],
      keyframeOptions: {
        duration: 200,
        easing: 'ease-out'
      }
    }) : animate({})}>
        <button type="button" @click=${this.handleBoardCreate} class="wishlist_board wishlist_board_skelly">${unsafeHTML(window.theme.strings.wishlist.create_board)}</button>
      </li>
    `;
  }

  renderBoardSelection() {
    return html`
      <div class="wishlist_board_selection">
        <h1 class="wishlist_header">
          ${unsafeHTML(window.theme.strings.wishlist.boards)}
        </h1>
        <div class="wishlist_boards_actions">
          <button class="btn btn-tertiary" type="button" @click=${this.handleBoardCreate} ?disabled=${this.loadingBoards || this.isInitalizing || this.boards?.length === window.wishlist.BOARD_CONFIG.MAX_BOARDS}>${unsafeHTML(window.theme.strings.wishlist.create_board)}</button>
        </div>
        <ul class="wishlist_board_grid">
          ${(this.loadingBoards && this.isInitialBoardLoad) || this.isInitializing
        ? this._renderBoardsLoading()
        : this.boards && this.boards.length > 0
          ? html`
                  ${repeat(
            this.boards,
            (board) => board.id,
            (board, index) => {
              this.renderedBoardIds.add(board.id);

              return html`
                <li class="wishlist_board_card">
                  ${this._renderBoard(board)}
                </li> `;
            }
          )}
                ${this.boards?.length !== window.wishlist.BOARD_CONFIG.MAX_BOARDS ? this.renderSkellyCreateBoard() : nothing}
                `
          : html`<li>No boards found</li>`}
        </ul>
      </div>
    `;
  }

  renderBoardProducts() {
    const isLoading =
      this.loadingBoardProducts ||
      this.loadingBoards ||
      (this.loading &&
        this.isInitialLoad &&
        (!this.displayedProducts || this.displayedProducts.length === 0));

    const colour =
      window.wishlist_colours.find((c) => c.value === this.currentBoard?.colour)
        ?.colour ||
      window.wishlist_colours.find((c) => c.default)?.colour ||
      this.currentBoard?.colour ||
      'white';

    return html`
      <div
        class="wishlist_board_banner ${this.currentBoard
          ? ''
          : 'wishlist_board_banner_skelly'}"
        style="--wishlist-colour: ${this.currentBoard ? colour : '#e9e9e9'};"
      >
        <h1 style="${this.currentBoard ? '' : 'color: rgba(0,0,0,0);'}">
          ${this.renderEmoji(this.currentBoard?.emoji, 32)}
          ${this.currentBoard?.name || 'Board'}
        </h1>
      </div>
      <div class="board_header">
        <button @click=${this.goBackToBoards} class="back_to_boards_btn">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12.0018 15.4949L6.32349 9.81663L7.64842 8.4917L12.0018 12.8451L16.3551 8.4917L17.6801 9.81663L12.0018 15.4949Z"
              fill="currentColor"
            />
          </svg>
          ${unsafeHTML(window.theme.strings.wishlist.back_to_boards)}
        </button>
        <wishlist-share-button board-id="${this.currentBoard?.id}"></wishlist-share-button>
      </div>
      ${window.Shopify.customer_logged_in ? html`
        <div class="wishlist_sub_header_text">${unsafeHTML(window.theme.strings.wishlist.sub_header_text)}</div>
      ` : nothing}
      <div class="wishlist_board_products">
        ${this.displayedProducts?.length > 0 || isLoading
          ? html`<ul class="wishlist_container">
                ${isLoading
                  ? this._renderWishlistLoading()
                  : this._renderWishlistContent()}
              </ul>
              ${this.showLoadMore ? this._renderLoadMoreButton() : nothing}`
          : this._renderWishlistEmpty()}
      </div>
    `;
  }

  render() {
    return html`<div class="wishlist_container_wrapper">
      ${!window.Shopify.customer_logged_in
        ? this._renderLoggedOut()
        : this._renderLoggedIn()}
      ${this.products?.length > 0
        ? html`<wishlist-toolbar
            .selectedProducts=${this.selectedProducts}
            .currentBoard=${this.currentBoard}
          ></wishlist-toolbar>`
        : nothing}
    </div>`;
  }

  renderEmoji(emoji, size = 24) {
    const emojiObj =
      window.wishlist_emojis.find((e) => e.value === emoji) ||
      window.wishlist_emojis.find((e) => e.default);

    if (!emojiObj) return nothing;

    if (emojiObj.type === 'svg') {
      return html`${unsafeHTML(emojiObj.display)}`;
    }

    if (emojiObj.type === 'image') {
      return html`<img
        src="${emojiObj.display}"
        width="${size}"
        height="${size}"
        alt="${emojiObj.value}"
        loading="lazy"
      />`;
    }

    if (emojiObj.type === 'emoji') {
      return html`<span style="font-size: ${size}px;"
        >${unsafeHTML(emojiObj.display)}</span
      >`;
    }
  }

  _renderBoard(board) {
    const previewProducts = this.boardPreviewCache.get(board.id) || [];

    const colour =
      window.wishlist_colours?.find((c) => c.value === board.colour)?.colour ||
      window.wishlist_colours?.find((c) => c.default)?.colour ||
      'white';

    return html`
      <div class="wishlist_board ${this.boardDeleting === board.id ? 'wishlist_board_deleting' : ''}" style="--wishlist-colour: ${colour}">
        <div class="wishlist_board_content">
          <div
            class="wishlist_board_title ${board.id === 1
              ? 'wishlist_board_title_default'
              : ''}"
          >
            <button type="button" class="wishlist_board_button" ?disabled=${this.boardDeleting === board.id} @click=${() => this.selectBoard(board.id)}>${this.renderEmoji(board.emoji, 16)}${' '}${board.name}</button>
            <div class="board_actions">
              ${board.id !== 1
        ? html`<button type="button" class="wishlist_delete" ?disabled=${this.boardDeleting === board.id} aria-label="Delete ${board.name}" @click=${() => this.handleBoardDelete(board)}>
                    <svg width="20" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9.93518 1.75C9.47279 1.74986 9.02174 1.89404 8.64416 2.16269C8.26658 2.43133 7.98106 2.81121 7.82693 3.25C7.75874 3.43443 7.62127 3.58449 7.44416 3.66783C7.26705 3.75117 7.06449 3.76111 6.88017 3.69551C6.69584 3.62991 6.54452 3.49401 6.45883 3.31714C6.37314 3.14026 6.35994 2.93658 6.42209 2.75C6.67897 2.01866 7.15479 1.38546 7.78404 0.937609C8.41329 0.489754 9.16501 0.249268 9.93568 0.249268C10.7064 0.249268 11.4581 0.489754 12.0873 0.937609C12.7166 1.38546 13.1924 2.01866 13.4493 2.75C13.5151 2.93764 13.5043 3.14393 13.419 3.3235C13.3338 3.50307 13.1812 3.6412 12.9947 3.7075C12.8083 3.7738 12.6033 3.76285 12.4249 3.67705C12.2465 3.59126 12.1093 3.43764 12.0434 3.25C11.8893 2.81121 11.6038 2.43133 11.2262 2.16269C10.8486 1.89404 10.3976 1.74986 9.93518 1.75ZM0.745117 5C0.745117 4.80109 0.823623 4.61032 0.963364 4.46967C1.1031 4.32902 1.29263 4.25 1.49026 4.25H18.3801C18.5777 4.25 18.7673 4.32902 18.907 4.46967C19.0467 4.61032 19.1252 4.80109 19.1252 5C19.1252 5.19891 19.0467 5.38968 18.907 5.53033C18.7673 5.67098 18.5777 5.75 18.3801 5.75H1.49026C1.29263 5.75 1.1031 5.67098 0.963364 5.53033C0.823623 5.38968 0.745117 5.19891 0.745117 5ZM3.88961 7.45C3.87643 7.25149 3.78545 7.06637 3.63667 6.93538C3.4879 6.80438 3.29351 6.73824 3.09628 6.7515C2.89906 6.76476 2.71514 6.85634 2.58499 7.00609C2.45485 7.15583 2.38913 7.35149 2.40231 7.55L2.8633 14.502C2.94775 15.784 3.0163 16.82 3.17726 17.634C3.34516 18.479 3.62931 19.185 4.21747 19.738C4.80564 20.291 5.52395 20.531 6.37341 20.642C7.19008 20.75 8.22136 20.75 9.49903 20.75H10.3723C11.649 20.75 12.6813 20.75 13.4979 20.642C14.3464 20.531 15.0657 20.292 15.6539 19.738C16.2411 19.185 16.5252 18.478 16.6931 17.634C16.8541 16.82 16.9216 15.784 17.0071 14.502L17.4681 7.55C17.4812 7.35149 17.4155 7.15583 17.2854 7.00609C17.1552 6.85634 16.9713 6.76476 16.7741 6.7515C16.5769 6.73824 16.3825 6.80438 16.2337 6.93538C16.0849 7.06637 15.9939 7.25149 15.9808 7.45L15.5237 14.35C15.4343 15.697 15.3707 16.635 15.2316 17.34C15.0955 18.025 14.9068 18.387 14.6355 18.643C14.3633 18.899 13.9917 19.065 13.3042 19.155C12.5958 19.248 11.6619 19.25 10.3197 19.25H9.55069C8.20944 19.25 7.27553 19.248 6.56615 19.155C5.87864 19.065 5.50706 18.899 5.23484 18.643C4.96361 18.387 4.77484 18.025 4.63872 17.34C4.49963 16.635 4.43605 15.697 4.34663 14.35L3.88961 7.45Z" fill="currentColor" />
                      <path d="M7.37687 9.254C7.57342 9.23417 7.76976 9.29372 7.92271 9.41954C8.07567 9.54537 8.17272 9.72717 8.19255 9.925L8.68931 14.925C8.70386 15.1201 8.6421 15.3132 8.51721 15.4631C8.39232 15.613 8.21418 15.7078 8.0208 15.7274C7.82741 15.747 7.63408 15.6897 7.48204 15.5678C7.33 15.4459 7.2313 15.2691 7.20697 15.075L6.71021 10.075C6.69052 9.87716 6.74968 9.67955 6.87468 9.52559C6.99969 9.37164 7.18032 9.27395 7.37687 9.254ZM13.1601 10.075C13.1747 9.87992 13.1129 9.68683 12.9881 9.53693C12.8632 9.38703 12.685 9.29218 12.4916 9.27261C12.2982 9.25305 12.1049 9.31031 11.9529 9.43218C11.8008 9.55406 11.7021 9.73091 11.6778 9.925L11.1811 14.925C11.1665 15.1201 11.2283 15.3132 11.3532 15.4631C11.478 15.613 11.6562 15.7078 11.8496 15.7274C12.043 15.747 12.2363 15.6897 12.3883 15.5678C12.5404 15.4459 12.6391 15.2691 12.6634 15.075L13.1601 10.075Z" fill="currentColor" />
                    </svg>
                  </button>`
        : nothing}
              <button type="button" class="wishlist_edit" ?disabled=${this.boardDeleting === board.id} aria-label="Edit ${board.name}" @click=${() => this.handleBoardEdit(board)}>
                <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8.4835 3.16668H5.18004C3.85853 3.16668 3.19778 3.16668 2.69303 3.42554C2.24904 3.65324 1.88807 4.01657 1.66185 4.46346C1.40466 4.9715 1.40466 5.63656 1.40466 6.96668V13.6167C1.40466 14.9468 1.40466 15.6119 1.66185 16.1199C1.88807 16.5668 2.24904 16.9301 2.69303 17.1578C3.19778 17.4167 3.85853 17.4167 5.18004 17.4167H11.787C13.1085 17.4167 13.7692 17.4167 14.274 17.1578C14.7179 16.9301 15.0789 16.5668 15.3051 16.1199C15.5623 15.6119 15.5623 14.9468 15.5623 13.6167V10.2917M6.12386 12.6667H7.44095C7.82571 12.6667 8.01809 12.6667 8.19914 12.6229C8.35965 12.5841 8.51309 12.5202 8.65384 12.4334C8.81259 12.3354 8.94862 12.1985 9.22068 11.9247L16.7421 4.35418C17.3937 3.69835 17.3937 2.63502 16.7421 1.97918C16.0905 1.32335 15.0341 1.32334 14.3825 1.97918L6.86105 9.54969C6.58899 9.82353 6.45296 9.96045 6.35567 10.1202C6.26942 10.2619 6.20586 10.4163 6.16733 10.5779C6.12386 10.7601 6.12386 10.9538 6.12386 11.341V12.6667Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </button>
            </div>
          </div>
          <button
            type="button"
            class="wishlist_board_button_content"
            @click=${() => this.selectBoard(board.id)}
            aria-label="Go to ${board.name}"
          >
            <span class="wishlist_board_count"
              >${board.itemCount}
              product${board.itemCount !== 1 ? 's' : nothing}</span
            >
            ${board.items.length > 0
        ? keyed(`preview-${board.id}-${board.itemCount}-${previewProducts.length}`, this._renderPreviewItems(board.id, board.itemCount, previewProducts))
        : nothing}
          </button>
        </div>
      </div>
    `;
  }

  _renderPreviewItems(boardId, itemCount, previewProducts) {
    const previewCount = Math.min(4, itemCount);
    return html`
      <div class="preview_items">
        ${repeat(
      Array.from({ length: previewCount }),
      (_, i) => `preview-${boardId}-${i}`,
      (_, i) => {
        const previewItem = previewProducts[i];
        const hasLoaded = previewItem && previewProducts.length > i;

        return html`
              <div class="preview_item">
                ${hasLoaded
            ? html`<picture>
                      <img src=${previewItem.product.featured_image}
                           alt=${previewItem.product.title}
                           loading="lazy"
                           width="75"
                           height="125"
                           sizes="(max-width: 768px) 75px, 75px" />
                    </picture>`
            : html`<div class="preview_item_skeleton"></div>`
          }
              </div>
            `;
      }
    )}
        <div class="preview_more" style="${itemCount > 4 ? '' : 'opacity: 0; pointer-events: none;'}">
          ${itemCount > 4 ? `+${itemCount - 4}` : ''}
        </div>
      </div>
    `;
  }

  _renderLoggedIn() {
    return html`${when(
      this.currentView === 'board-selection',
      () => this.renderBoardSelection(),
      () => this.renderBoardProducts()
    )}`;
  }

  _renderLoggedOut() {
    return html`${this.isInitialLoad || this.wishlist?.count > 0
      ? html` <div class="wishlist_logged_out_content wishlist_loading ${
          !this.loading ? 'wishlist_loaded' : ''
        }">
            <h1 class="wishlist_logged_out_title">${unsafeHTML(
              window.theme.strings.wishlist.title
            )}</h1>
            <div class="wishlist_logged_out_login">${unsafeHTML(
              window.theme.strings.wishlist.login_message_html
            )}</div>
          </div>
          <ul class="wishlist_container">
            ${
              this.loading &&
              this.isInitialLoad &&
              (!this.displayedProducts || this.displayedProducts.length === 0)
                ? this._renderWishlistLoading()
                : this._renderWishlistContent()
            }
          </ul>
      ${this.showLoadMore ? this._renderLoadMoreButton() : nothing}
        </div>`
      : this._renderWishlistEmpty()}`;
  }

  _renderWishlistEmpty() {
    return html` <div
      class="wishlist_empty_wrapper wishlist_loading ${!this.loading
        ? 'wishlist_loaded'
        : ''}"
    >
      <p class="wishlist_empty_title">
        ${unsafeHTML(window.theme.strings.wishlist.no_item_title)}
      </p>
      <div class="wishlist_empty_actions">
        ${window.Shopify.customer_logged_in
          ? nothing
          : unsafeHTML(window.theme.strings.wishlist.login_button_html)}
        <a
          class="btn btn-tertiary"
          href="${`${window.Shopify.routes.root}collections/new-arrivals`}"
          >${window.theme.strings.wishlist.shop_now}
        </a>
      </div>
    </div>`;
  }

  _renderWishlistLoading() {
    const lastCount = this.lastDisplayedCount || 0;
    const plannedCount = this.itemsToShow || 20;
    const maxCount =
      window.Shopify.customer_logged_in && this.currentView === 'board-products'
        ? this.currentBoard?.items?.length || plannedCount
        : this.wishlist?.count || 0;

    const skeletonCount = Math.min(Math.max(lastCount, plannedCount), maxCount);

    return html`${repeat(
      [...Array(skeletonCount)],
      () =>
        html`<li>
          <product-card-skeleton swatches="false"></product-card-skeleton>
        </li>`
    )}`;
  }

  _renderBoardsLoading() {
    const lengthOrZero = this.wishlist?.boards?.length + 1 || 0;
    const boardSkeletonCount = Math.min(12, lengthOrZero);
    return html`${repeat(
      [...Array(boardSkeletonCount)],
      (_, index) => `board-skeleton-${index}`,
      (_, index) => html`<li class="board_card_skeleton"></li>`,
    )}`;
  }

  _renderWishlistContent() {
    const products = this.displayedProducts || [];

    const productItems = repeat(
      products,
      (item) => item.product.id,
      (item, index) => {
        const productId = item.product.id;
        const originalId = item.originalId || item.product.id;
        const isBeingRemoved = this.removingProductIds.has(productId);

        if (isBeingRemoved) {
          return html`
            <li class="wishlist_item_removing">
              <product-card-skeleton swatches="false"></product-card-skeleton>
            </li>
          `;
        }

        const stablePosition = this.productPositions.get(productId) || index;
        const isFirstRender = !this.renderedProductIds.has(productId);

        this.renderedProductIds.add(productId);

        const animationStyle = isFirstRender
          ? `animation-delay: ${(stablePosition % 20) * 0.20}s;`
          : '';

        return html`
          <li class="wishlist_item"
              style="${animationStyle}"
              ${animate({
          out: [
            { opacity: 1 },
            { opacity: 0 }
          ],
          keyframeOptions: {
            duration: 200,
            easing: 'ease-out'
          }
        })}>
            <product-card
              .discount=${this.discount}
              .siblings=${item.siblings}
              .product=${item.product}
              ?selected-variant-id="${item.selectedVariant?.id}"
              .initialSelectedVariant=${item.selectedVariant}
              .selected=${this.selectedProducts.has(originalId)}
              wishlist-features="true"
              wishlist-id="${ifDefined(this.currentBoard?.id)}"
              mode="variant-select"
              loading="${index < 8 ? 'eager' : 'lazy'}"
            >
            </product-card>
          </li>
        `;
      }
    );

    const loadingSkeletons =
      this.isLoadingMore && this.showLoadMore
        ? this._renderNextBatchSkeletons()
        : '';

    return html`${productItems}${loadingSkeletons}`;
  }

  _renderNextBatchSkeletons() {
    const nextBatchSize = Math.min(
      20,
      this.products?.length - this.displayedProducts?.length || 0
    );
    if (nextBatchSize <= 0) return '';

    const currentDisplayedCount = this.displayedProducts?.length || 0;

    return html`${repeat(
      [...Array(nextBatchSize)],
      (_, index) => `loading-${currentDisplayedCount + index}`,
      (_, index) => {
        const skeletonPosition = currentDisplayedCount + index;
        return html`
          <li key="loading-${skeletonPosition}"
              class="wishlist_item_loading"
              ${animate({
          in: [
            { opacity: 0 },
            { opacity: 1 }
          ],
          keyframeOptions: {
            duration: 150,
            easing: 'ease-out'
          }
        })}>
            <product-card-skeleton swatches="false"></product-card-skeleton>
          </li>
        `;
      }
    )}`;
  }

  _renderLoadMoreButton() {
    return html`
      <div class="wishlist_load_more_container">
        <button
          class="wishlist_load_more_btn btn btn-primary"
          @click=${this.loadMore}
          ?disabled=${this.isLoadingMore}
        >
          ${this.isLoadingMore
            ? html`${unsafeHTML(window.theme.strings.wishlist.loading)}`
            : html`${unsafeHTML(window.theme.strings.wishlist.load_more)}`}
        </button>
      </div>
    `;
  }
}

window.customElements.get('wishlist-main') ||
  window.customElements.define('wishlist-main', WishlistMain);
